import React, { useState } from 'react';
import { Wand2, Loader2, ChevronDown, Check, X } from 'lucide-react';
import { Button } from '../ui/button';
import { dealerListings } from '../../services/dealerApi';
import { toast } from '../../hooks/use-toast';
import { SPEC_FIELDS, GROUP_TITLES, GROUP_ORDER, groupItems, formatSpec } from '../../utils/vehicleFeatures';

/**
 * Read-only intelligence panel used inside the dealer Approve page.
 * Shows fetched specs + features and allows the dealer to refresh
 * after editing the make/model/variant.
 */
const VehicleIntelligencePanel = ({ listingId, intelligence, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);

  const handleRefresh = async (force = false) => {
    setLoading(true);
    try {
      const res = await dealerListings.fetchIntelligence(listingId, force);
      toast({ title: force ? 'Refreshed from latest data' : 'Vehicle intelligence loaded' });
      onRefresh && onRefresh(res.intelligence);
    } catch (err) {
      toast({
        title: 'Could not fetch intelligence',
        description: err?.response?.data?.detail || 'Check make/model and try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const specs = intelligence?.specifications || {};
  const features = intelligence?.features || {};
  const highlights = intelligence?.key_highlights || [];

  const hasSpecs = SPEC_FIELDS.some(f => specs[f.key] !== null && specs[f.key] !== undefined && specs[f.key] !== '');

  if (!intelligence) {
    return (
      <div data-testid="intel-panel-empty" className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-cyan-400" /> Vehicle Intelligence
            </h2>
            <p className="text-xs text-slate-400 mt-1 max-w-xl">
              Auto-fetch variant-specific specs &amp; features to enrich the buyer-facing page.
              Requires Make and Model to be set.
            </p>
          </div>
          <Button
            type="button"
            onClick={() => handleRefresh(false)}
            disabled={loading}
            data-testid="intel-fetch-btn"
            className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-xl"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
            Fetch Intelligence
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="intel-panel" className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-semibold text-white flex items-center gap-2">
            <Wand2 className="w-4 h-4 text-cyan-400" /> Vehicle Intelligence
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            Confidence: <span className="text-cyan-400">{Math.round((intelligence.confidence || 0) * 100)}%</span>
            {intelligence.notes ? <span className="ml-3 text-amber-400/80">{intelligence.notes}</span> : null}
          </p>
        </div>
        <Button
          type="button"
          onClick={() => handleRefresh(true)}
          disabled={loading}
          variant="outline"
          data-testid="intel-refetch-btn"
          className="border-slate-700 bg-slate-900/40 text-slate-200"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-2" />}
          Refetch
        </Button>
      </div>

      {/* Key Highlights chips */}
      {highlights.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-mono mb-2">Key Highlights</div>
          <div className="flex flex-wrap gap-2" data-testid="intel-highlights">
            {highlights.map((h) => (
              <span key={h.key} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-200 text-xs font-medium">
                <Check className="w-3 h-3" strokeWidth={3} />
                {h.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Specifications */}
      {hasSpecs && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-mono mb-2">Specifications</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="intel-specs">
            {SPEC_FIELDS.map((f) => {
              const val = formatSpec(specs[f.key], f.suffix || '');
              if (!val) return null;
              return (
                <div key={f.key} className="px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-800">
                  <div className="text-[9px] uppercase tracking-wider text-slate-500">{f.label}</div>
                  <div className="text-sm font-semibold text-white mt-0.5 truncate">{val}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Feature groups (collapsible) */}
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-mono">All Features</div>
        {GROUP_ORDER.map((g) => {
          const items = groupItems(g, features[g] || {});
          const onCount = items.filter((i) => i.isOn).length;
          const isOpen = openGroup === g;
          return (
            <div key={g} className="border border-slate-800 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setOpenGroup(isOpen ? null : g)}
                data-testid={`intel-group-${g}`}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-950/60 hover:bg-slate-950 text-left"
              >
                <span className="text-sm font-semibold text-white">{GROUP_TITLES[g]}</span>
                <span className="flex items-center gap-2 text-xs text-slate-400">
                  <span className="font-mono">{onCount}/{items.length}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </span>
              </button>
              {isOpen && (
                <div className="px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5 bg-slate-950/30">
                  {items.map((it) => (
                    <div key={it.key} className="flex items-center gap-2 text-xs">
                      {it.isOn
                        ? <Check className="w-3.5 h-3.5 text-emerald-400" strokeWidth={3} />
                        : <X className="w-3.5 h-3.5 text-slate-600" strokeWidth={3} />}
                      <span className={it.isOn ? 'text-slate-200' : 'text-slate-500 line-through'}>
                        {it.label}{it.valueLabel ? ` · ${it.valueLabel}` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VehicleIntelligencePanel;
